import { useEffect, useState } from "react";
import { Settings as Gear, UserPlus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Section, Field, Pill } from "@/components/common";
import Modal from "@/components/Modal";
import BiometricCard from "@/components/BiometricCard";
import { PageTitle, Table, Td } from "@/pages/admin/ui";

function Company() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Loader />;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const save = async () => {
    try { setS((await api.put("/settings", { ...s, doc_alert_days: Number(s.doc_alert_days) })).data); toast.success("تم حفظ الإعدادات"); } catch (e) { toast.error(errMsg(e)); }
  };
  const ft = (i, k, v) => setS({ ...s, fee_types: s.fee_types.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  return (
    <Section title="بيانات الشركة">
      <div className="grid sm:grid-cols-2 gap-3">
        {[["company_name", "اسم الشركة"], ["company_name_en", "الاسم بالإنجليزية"], ["phone", "الهاتف"], ["address", "العنوان"], ["cr_number", "رقم السجل التجاري"], ["invoice_footer", "تذييل الفاتورة"]].map(([k, l]) => (
          <Field key={k} label={l}><input data-testid={`set-${k}`} className="field field-sm" value={s[k] || ""} onChange={set(k)} /></Field>))}
        <Field label="التنبيه قبل انتهاء المستند (يوم)"><input data-testid="set-doc-alert-days" type="number" className="field field-sm" value={s.doc_alert_days} onChange={set("doc_alert_days")} /></Field>
      </div>
      <div className="mt-5">
        <b>أنواع الرسوم الإدارية</b>
        <p className="text-sm text-slate-500 mb-2">راجع كل نوع وفق القوانين والعقود المعمول بها في الكويت قبل تحميله على السائقين، ثم ضع علامة «تمت المراجعة».</p>
        <div className="space-y-2">{s.fee_types.map((t, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input data-testid={`fee-type-label-${i}`} className="field field-sm flex-1" value={t.label} onChange={(e) => ft(i, "label", e.target.value)} />
            <label className="flex items-center gap-1 text-sm whitespace-nowrap"><input type="checkbox" checked={!!t.reviewed} onChange={(e) => ft(i, "reviewed", e.target.checked)} />تمت المراجعة</label>
            <button onClick={() => setS({ ...s, fee_types: s.fee_types.filter((_, j) => j !== i) })} className="text-red-600"><Trash2 className="w-5 h-5" /></button>
          </div>))}</div>
        <button data-testid="add-fee-type" onClick={() => setS({ ...s, fee_types: [...s.fee_types, { key: "", label: "", reviewed: false }] })} className="btn btn-sm btn-ghost mt-2"><Plus className="w-4 h-4" />نوع جديد</button>
      </div>
      <button data-testid="save-settings-btn" onClick={save} className="btn btn-md btn-dark mt-5">حفظ الإعدادات</button>
    </Section>
  );
}

function Staff() {
  const [d, setD] = useState(null);
  const [m, setM] = useState(null);
  const load = () => api.get("/staff").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return <Loader />;
  const save = async () => {
    try {
      if (m.id) await api.patch(`/staff/${m.id}`, m); else await api.post("/staff", m);
      toast.success("تم الحفظ"); setM(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (p) => setM({ ...m, permissions: m.permissions.includes(p) ? m.permissions.filter((x) => x !== p) : [...m.permissions, p] });
  return (
    <Section title="موظفو الإدارة والصلاحيات" action={<button data-testid="add-staff-btn" onClick={() => setM({ name: "", phone: "", password: "", permissions: [], status: "active" })} className="btn btn-sm btn-blue"><UserPlus className="w-4 h-4" />إضافة موظف</button>}>
      <Table head={["الاسم", "الهاتف", "الدور", "الصلاحيات", "الحالة", ""]} testid="staff-table">
        {d.items.map((u) => <tr key={u.id}><Td className="font-bold">{u.name}</Td><Td><span className="ltr">{u.phone}</span></Td>
          <Td>{u.role === "admin" ? "مدير النظام" : "موظف إدارة"}</Td>
          <Td className="max-w-md">{u.role === "admin" ? "جميع الصلاحيات" : (u.permissions || []).map((p) => d.permissions[p]).join("، ")}</Td>
          <Td><Pill color={u.status === "active" ? "green" : "red"}>{u.status === "active" ? "نشط" : "موقوف"}</Pill></Td>
          <Td>{u.role === "staff" && <button data-testid={`edit-staff-${u.phone}`} onClick={() => setM({ ...u, password: "" })} className="btn btn-sm btn-ghost">تعديل</button>}</Td></tr>)}
      </Table>
      <Modal open={!!m} onClose={() => setM(null)} title={m?.id ? "تعديل موظف" : "إضافة موظف"} testid="staff-modal">
        {m && <div className="space-y-3">
          <Field label="الاسم"><input data-testid="staff-name" className="field field-sm" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} /></Field>
          <Field label="الهاتف (للدخول)"><input data-testid="staff-phone" className="field field-sm ltr text-left" value={m.phone} onChange={(e) => setM({ ...m, phone: e.target.value })} /></Field>
          <Field label={m.id ? "كلمة مرور جديدة (اختياري)" : "كلمة المرور"}><input data-testid="staff-password" className="field field-sm" value={m.password} onChange={(e) => setM({ ...m, password: e.target.value })} /></Field>
          <div className="grid sm:grid-cols-2 gap-2">{Object.entries(d.permissions).map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input data-testid={`perm-${k}`} type="checkbox" className="w-5 h-5" checked={m.permissions.includes(k)} onChange={() => toggle(k)} />{l}</label>))}</div>
          {m.id && <Field label="الحالة"><select className="field field-sm" value={m.status} onChange={(e) => setM({ ...m, status: e.target.value })}><option value="active">نشط</option><option value="suspended">موقوف</option></select></Field>}
          <button data-testid="staff-save" onClick={save} className="btn btn-lg btn-blue w-full">حفظ</button>
        </div>}
      </Modal>
    </Section>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  return (
    <div className="space-y-5"><PageTitle title="الإعدادات" icon={Gear} />
      <BiometricCard loginId={user.username || user.phone} />
      {admin && <><Company /><Staff /></>}
    </div>
  );
}
